/**
 * The backup folder (decision 72; part 24e log, tables W, R, K, principle 1): a File System Access directory the writer
 * picked, as far as backups use it: what a name holds, a whole file written under a name, a file removed. Its failures
 * are classed by the `DOMException` name the browser throws (W6). The handle is read by shape at the boundary, since it
 * comes back from IndexedDB and across a worker's message.
 */

/** A file's size and last-modified time, as the folder reports them. */
export interface Found {
  readonly size: number;
  readonly modified: number;
}

/** W6: why the folder refused: the permission (N2), the folder gone (N3), full (N4), or anything else (N5). */
export type FolderFailure = 'permission' | 'gone' | 'full' | 'failed';

/** A folder failure, classed (W6). */
export class FolderError extends Error {
  readonly reason: FolderFailure;

  constructor(reason: FolderFailure, cause: unknown) {
    super(`backup folder: ${reason}`, { cause });
    this.name = 'FolderError';
    this.reason = reason;
  }
}

/** S10 (deep-fix r3): the folder refused a name (a `TypeError` from `getFileHandle`): that book's failure, not the folder's. */
export class NameRefusedError extends Error {
  override readonly name = 'NameRefusedError';

  constructor(refused: string, cause: unknown) {
    super(`the backup folder refused the name "${refused.slice(0, 64)}"`, { cause });
  }
}

/** The folder, as backups use it. */
export interface Folder {
  /** What `name` holds: a file's size and time, `other` for anything but a file, null for nothing. */
  stat(name: string): Promise<Found | 'other' | null>;
  /** W4, W6: writes `file` whole under `name` (made new; the caller has checked it is free) and reports it as written. */
  write(name: string, file: Blob): Promise<Found>;
  /** K2, R4: removes the file `name`; a name already gone is no failure. */
  remove(name: string): Promise<void>;
}

/** The writable stream a file handle opens (`FileSystemWritableFileStream`), as far as backups use it. */
export interface WritableLike {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

/** A file handle (`FileSystemFileHandle`), as far as backups use it. */
export interface FileHandleLike {
  getFile(): Promise<{ readonly size: number; readonly lastModified: number }>;
  createWritable(): Promise<WritableLike>;
}

/** A directory handle (`FileSystemDirectoryHandle`), as far as backups use it. */
export interface DirectoryLike {
  readonly kind: 'directory';
  readonly name: string;
  getFileHandle(name: string, options?: { readonly create?: boolean }): Promise<FileHandleLike>;
  removeEntry(name: string): Promise<void>;
  queryPermission(descriptor: { readonly mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission(descriptor: { readonly mode: 'readwrite' }): Promise<PermissionState>;
  /** O2′ (deep-fix r5): whether `other` is the same folder (two reads of a stored handle are two objects). */
  isSameEntry(other: DirectoryLike): Promise<boolean>;
}

/** N7: whether `value` is a directory handle: a `directory` with the methods backups call. */
export function isDirectory(value: unknown): value is DirectoryLike {
  if (typeof value !== 'object' || value === null) return false;
  return (
    Reflect.get(value, 'kind') === 'directory' &&
    typeof Reflect.get(value, 'name') === 'string' &&
    ['getFileHandle', 'removeEntry', 'queryPermission', 'requestPermission', 'isSameEntry'].every((method) => typeof Reflect.get(value, method) === 'function')
  );
}

/** The `DOMException` name of `error`, or ''. */
function errorName(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const name: unknown = Reflect.get(error, 'name');
  return typeof name === 'string' ? name : '';
}

/** W6: `error` classed. */
export function folderFailure(error: unknown): FolderFailure {
  switch (errorName(error)) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permission';
    case 'NotFoundError':
      return 'gone';
    case 'QuotaExceededError':
      return 'full';
    default:
      return 'failed';
  }
}

/** The folder over `directory`. */
export function directoryFolder(directory: DirectoryLike): Folder {
  const stat = async (name: string): Promise<Found | 'other' | null> => {
    let handle: FileHandleLike;
    try {
      handle = await directory.getFileHandle(name);
    } catch (error) {
      const named = errorName(error);
      if (named === 'NotFoundError') return null;
      // A folder (or anything not a file) under the name.
      if (named === 'TypeMismatchError') return 'other';
      if (named === 'TypeError') throw new NameRefusedError(name, error);
      throw new FolderError(folderFailure(error), error);
    }
    try {
      const file = await handle.getFile();
      return { size: file.size, modified: file.lastModified };
    } catch (error) {
      throw new FolderError(folderFailure(error), error);
    }
  };
  const remove = async (name: string): Promise<void> => {
    try {
      await directory.removeEntry(name);
    } catch (error) {
      if (errorName(error) !== 'NotFoundError') throw new FolderError(folderFailure(error), error);
    }
  };
  return {
    stat,
    remove,
    async write(name, file) {
      let handle: FileHandleLike;
      try {
        handle = await directory.getFileHandle(name, { create: true });
      } catch (error) {
        if (errorName(error) === 'TypeError') throw new NameRefusedError(name, error);
        throw new FolderError(folderFailure(error), error);
      }
      let stream: WritableLike | null = null;
      try {
        stream = await handle.createWritable();
        await stream.write(file);
        const closing = stream;
        stream = null;
        await closing.close();
        const written = await handle.getFile();
        return { size: written.size, modified: written.lastModified };
      } catch (error) {
        // W6: nothing of the bundle reaches the name (the stream's swap file goes with `abort`); the empty file
        // `getFileHandle` made goes too, while it is still empty (principle 1).
        await stream?.abort().catch(() => undefined);
        await removeEmpty(stat, remove, name);
        throw new FolderError(folderFailure(error), error);
      }
    },
  };
}

/** W6, R4: removes `name` if it is an empty file; nothing else, and no failure of its own. */
async function removeEmpty(stat: Folder['stat'], remove: Folder['remove'], name: string): Promise<void> {
  try {
    const found = await stat(name);
    if (found !== null && found !== 'other' && found.size === 0) await remove(name);
  } catch {
    // R4: the next run removes it.
  }
}
